import React, { useEffect, useState } from 'react';

import { BannerSeverity } from '@rover/kibble/patterns/Banner/BannerSeverity.constants';
import Toast from '@rover/kibble/patterns/Toast';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import { MetaDiscountConfirmationToastShow } from './analyticsEvents';
import { META_DISCOUNT_AMOUNT_LABEL } from './constants';
import { getSessionId, hasClaimMarker } from './helpers';

/**
 * Post-signup surface: a one-time green confirmation toast shown when an
 * authenticated visitor returns to the geo-service page after taking the offer
 * (detected via the claim marker on the URL, not the experiment — so no extra
 * exposure is logged for authenticated users).
 */
const MetaDiscountConfirmation = (): JSX.Element | null => {
  const { user } = useCurrentUser();
  const [isVisible, setIsVisible] = useState(false);

  // Read the claim marker on the client only (this page is server-rendered, so
  // touching window at render time would crash SSR). The effect also keeps the
  // toast a one-time, post-signup surface — no experiment is read here.
  useEffect(() => {
    if (!hasClaimMarker(window.location.search)) return;
    setIsVisible(true);
    emitAnalyticsEvent(
      new MetaDiscountConfirmationToastShow({ session_id: getSessionId(), person_id: user?.opk })
    );
    // Fire once on mount; user?.opk is best-effort if already resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isVisible) return null;

  // Float as a one-time toast pinned to the viewport bottom — it slides in,
  // auto-hides, and is dismissible. `position: fixed` (overriding the Toast's
  // default `absolute`) keeps it anchored to the viewport since this surface is
  // mounted at the top of the page flow with no positioned ancestor.
  return (
    <Toast
      severity={BannerSeverity.SUCCESS}
      title={`You have a ${META_DISCOUNT_AMOUNT_LABEL} credit!`}
      onClose={() => setIsVisible(false)}
      containerProps={{
        position: 'fixed',
        left: '0',
        right: '0',
        zIndex: '1000',
        sx: { marginInline: 'auto' },
      }}
    >
      Check the notification center and use the code at checkout. Terms apply.
    </Toast>
  );
};

export default MetaDiscountConfirmation;
